// ===========================================================================
// SocialStratificationPanel — editor de las 7 dimensiones SDOH + categoría
// global. Se abre como drawer desde la ficha. El equipo revisa/corrige lo que
// el sistema pre-sugirió.
//
// ⚠️ Los endpoints de guardar (dimensión/global) son CONTRATO PROPUESTO y aún
// no existen en backend. Por eso Save muestra un aviso "pending backend" si la
// llamada falla. El POST "suggest" (documentado) sí es real.
// Ver socialStratificationService.js y OBSERVACIONES_FRONTEND_HIGH_COST.md.
// ===========================================================================
import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Divider,
  TextField,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  saveSocialDimension,
  saveSocialGlobal,
  suggestSocialStratification,
} from "../../services";
import { SOCIAL_CATEGORY_COLORS } from "../../common/constants";
import DimensionCard from "./DimensionCard";

const GLOBAL_OPTIONS = ["Stable", "Vulnerable", "High Social Risk", "Critical", "Unclassified"];
const keyOf = (label) => label.toLowerCase().replace(/\s+/g, "_");

export default function SocialStratificationPanel({ open, onClose, cipId, profile }) {
  const [saving, setSaving] = useState(false);
  const [globalCategory, setGlobalCategory] = useState(profile?.globalCategory || "Unclassified");
  const [globalJustification, setGlobalJustification] = useState("");
  const [snack, setSnack] = useState(null); // { severity, message }

  const dimensions = profile?.dimensions || [];
  const globalDirty = globalCategory !== (profile?.globalCategory || "Unclassified");

  const handleSaveDimension = async (dimensionKey, level, justification) => {
    setSaving(true);
    try {
      const res = await saveSocialDimension(cipId, { dimensionKey, level, justification });
      setSnack(
        res?.result === 1
          ? { severity: "success", message: "Dimension saved." }
          : { severity: "warning", message: res?.message || "Save returned an error." }
      );
    } catch {
      setSnack({ severity: "info", message: "Save endpoint pending backend (proposed contract)." });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGlobal = async () => {
    setSaving(true);
    try {
      const res = await saveSocialGlobal(cipId, {
        globalCategoryKey: keyOf(globalCategory),
        justification: globalJustification.trim(),
      });
      setSnack(
        res?.result === 1
          ? { severity: "success", message: "Global category saved." }
          : { severity: "warning", message: res?.message || "Save returned an error." }
      );
    } catch {
      setSnack({ severity: "info", message: "Save endpoint pending backend (proposed contract)." });
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    setSaving(true);
    try {
      const res = await suggestSocialStratification(cipId);
      setSnack(
        res?.result === 1
          ? { severity: "success", message: res.message || "Suggestion generated." }
          : { severity: "warning", message: res?.message || "Suggestion failed." }
      );
    } catch {
      setSnack({ severity: "error", message: "Could not run suggestion." });
    } finally {
      setSaving(false);
    }
  };

  const socColor = SOCIAL_CATEGORY_COLORS[globalCategory] || "#9e9e9e";

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 520, maxWidth: "100%" } }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Social stratification
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          The system suggests; the clinician confirms or corrects. Suggestions are indirect proxies, not facts.
        </Alert>

        {/* Categoría global actual */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Current global category:
          </Typography>
          <Chip label={profile?.globalCategory || "Unclassified"} size="small" sx={{ color: "#fff", bgcolor: SOCIAL_CATEGORY_COLORS[profile?.globalCategory] || "#9e9e9e", fontWeight: 600 }} />
          <Button size="small" startIcon={<AutoFixHighIcon />} onClick={handleSuggest} disabled={saving} sx={{ ml: "auto", textTransform: "none" }}>
            Re-run suggestion
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* 7 dimensiones */}
        <Stack spacing={1.5}>
          {dimensions.map((d) => (
            <DimensionCard key={d.key} dimension={d} onSave={handleSaveDimension} saving={saving} />
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Override de categoría global */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Override global category
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          {GLOBAL_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              onClick={() => setGlobalCategory(opt)}
              variant={globalCategory === opt ? "filled" : "outlined"}
              sx={globalCategory === opt ? { bgcolor: socColor, color: "#fff", fontWeight: 600 } : {}}
            />
          ))}
        </Stack>
        {globalDirty && (
          <Box>
            <TextField
              size="small"
              fullWidth
              required
              multiline
              minRows={2}
              label="Justification (required)"
              value={globalJustification}
              onChange={(e) => setGlobalJustification(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!globalJustification.trim() || saving}
                onClick={handleSaveGlobal}
                sx={{ textTransform: "none" }}
              >
                Save global category
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ width: "100%" }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Drawer>
  );
}
